import { Badge, ThemeProvider, Box } from '@mui/material';
import { baseTheme } from 'design-system';
import { useFormDialog } from 'providers/FormDialogProvider';
import { MdOutlineCompareArrows } from 'react-icons/md';
import { getTheme } from 'services/theming';
import useUser from 'hooks/useUser';
import { AdminProfile, FormType } from 'generated/graphql';
import freshProduce from './ProductIcon/fresh-produce.svg';
import inputs from './ProductIcon/inputs.svg';
import { useWhiteLabelling } from './WhiteLabellingProvider';

const PROFILE_SWITCH_DIAMETER = 5;

export enum ProfileSwitchSize {
  Default = 'default',
  Small = 'small',
}

const ProfileSwitch = ({
  size = ProfileSwitchSize.Default,
  isButton = false,
}: {
  size?: ProfileSwitchSize;
  isButton?: boolean;
}) => {
  const { user, isSeller, isBuyer, isAdmin, isNileUser } = useUser();
  const { isInputsProfile } = useWhiteLabelling();
  const iconSRC = isInputsProfile ? inputs : freshProduce;
  // Admins are not linked one-to-one, so a second admin profile on the other platform only
  // exists when the user is linked to more than one profile.
  const showAdminSwitch = isButton && isAdmin && !!user?.multiProfile && (isNileUser || isInputsProfile);
  const showArrows =
    (isButton && isSeller && isNileUser) || (isButton && isBuyer && isInputsProfile) || showAdminSwitch;
  const showBadge = !isButton || showArrows;
  const { openFormDialog } = useFormDialog();

  const getBadgeTheme = () => {
    if (((isSeller && isNileUser) || (showAdminSwitch && isNileUser)) && isButton)
      return getTheme(AdminProfile.NileInputs);
    else if (((isBuyer && isInputsProfile) || (showAdminSwitch && isInputsProfile)) && isButton)
      return getTheme(AdminProfile.Nile);
    else return baseTheme;
  };

  const handleShowDialog = () => {
    openFormDialog({
      open: true,
      type: FormType.ProfileQuickActions,
      editIDs: [],
      hideDialogActions: true,
    });
  };

  return (
    <Box
      sx={(theme) => ({
        borderRadius: '50%',
        backgroundColor: isInputsProfile ? theme.palette.secondary.main : theme.palette.primary.light,
        width: theme.spacing(PROFILE_SWITCH_DIAMETER),
        height: theme.spacing(PROFILE_SWITCH_DIAMETER),
        minWidth: theme.spacing(PROFILE_SWITCH_DIAMETER),
        minHeight: theme.spacing(PROFILE_SWITCH_DIAMETER),
        transform: size === ProfileSwitchSize.Small ? 'scale(0.7)' : 'scale(0.9)',
        cursor: isButton ? 'pointer' : 'default',
      })}
      onClick={() => {
        if (isButton) handleShowDialog();
      }}
    >
      <ThemeProvider theme={getBadgeTheme()}>
        {showBadge ? (
          <Badge
            overlap="circular"
            badgeContent={showArrows ? <MdOutlineCompareArrows size={16} /> : ''}
            sx={(theme) => ({
              '& > span': {
                transform: isButton ? 'translate(90%, 100%)' : 'scale(0.5) translate(150%, 250%)',
                background: showArrows
                  ? isNileUser
                    ? theme.palette.secondary.light
                    : theme.palette.primary.light
                  : theme.palette.primary.main, // TODO: Confirm colouring here
                border: isButton ? 'inherit' : `3px solid ${theme.palette.background.paper}`,
                padding: 0,
              },
            })}
          >
            <Box
              component="img"
              src={iconSRC}
              sx={{
                width: 30,
                height: 30,
                transform: 'translate(15%, 15%)',
              }}
            />
          </Badge>
        ) : (
          <Box
            component="img"
            src={iconSRC}
            sx={{
              width: 30,
              height: 30,
              transform: 'translate(15%, 15%)',
            }}
          />
        )}
      </ThemeProvider>
    </Box>
  );
};

export default ProfileSwitch;
